import { nodesFlux, useEditorSelector } from '@candulabs/craft';
import groupBy from 'lodash/groupBy';
import React, { useEffect, useMemo } from 'react';
import { createSelector } from 'reselect';

import { getNodeId } from '@candu-ai/styleguide/selectors';
import { getBreakpointIndex, useViewportBreakpoint } from 'craft/state';
import { CraftNode, CssSelector, CssSelectorType, MediaBreakpoint } from 'craft/types';

const STYLESHEET_ID = 'cssStylesheets';

const selectCssSelectors = createSelector(
  nodesFlux.selectors.getNodes,
  (nodes: Record<string, CraftNode>) =>
    Object.entries(nodes)
      .map(([nodeId, node]) => {
        const { cssSelectors } = node?.data?.custom ?? {};
        if (!cssSelectors) {
          return null;
        }
        // avoid selecting responsive objects as we already handle them differently
        const selectors = Object.values(cssSelectors).filter(
          (s: CssSelector) => s.type !== CssSelectorType.Root && s.selector,
        );
        if (selectors.length < 1) {
          return null;
        }
        return { nodeId, cssSelectors: selectors };
      })
      .filter((n) => !!n),
);

const cssToString = (css: object) => {
  return Object.keys(css).reduce(
    (acc, key) =>
      `${
        acc +
        key
          .split(/(?=[A-Z])/)
          .join('-')
          .toLowerCase()
      }:${(css as any)[key]};`,
    '',
  );
};

const generateStylesheet = (activeViewport: MediaBreakpoint, nodesWithSelectors: CssSelector[]) => {
  let injectSheet = '';
  const viewportIndex = getBreakpointIndex(activeViewport);

  nodesWithSelectors.forEach(({ nodeId, cssSelectors }: any) => {
    Object.values(groupBy(cssSelectors, 'selector')).forEach((selectorGroup) => {
      let css = {};
      selectorGroup
        .sort((one, two) => getBreakpointIndex(one.viewport) - getBreakpointIndex(two.viewport))
        .forEach((selector) => {
          if (getBreakpointIndex(selector.viewport) > viewportIndex) {
            return;
          }
          css = { ...css, ...(selector.css ?? {}) };
        });
      const { selector } = selectorGroup[0];
      injectSheet += `
      #${getNodeId(nodeId)} ${selector} {
        ${cssToString(css)}
      }
    `;
    });
  });

  return injectSheet;
};

export const CssSelectorStylesheets = React.memo(() => {
  const viewport = useViewportBreakpoint();
  const nodesWithSelectors = useEditorSelector(selectCssSelectors);

  const injectSheet = useMemo(() => generateStylesheet(viewport, nodesWithSelectors), [
    nodesWithSelectors,
    viewport,
  ]);

  useEffect(() => {
    const head = document.head || document.getElementsByTagName('head')[0];
    let style = document.getElementById(STYLESHEET_ID);
    if (style) {
      style.textContent = injectSheet;
    } else {
      style = document.createElement('style');
      style.id = STYLESHEET_ID;
      // @ts-expect-error
      style.type = 'text/css';
      style.textContent = injectSheet;
      head.append(style);
    }
  }, [injectSheet]);

  return null;
});

CssSelectorStylesheets.displayName = 'CssSelectorStylesheets';
